import React, { useEffect, useState } from "react";
import {
  Card,
  DatePicker,
  Space,
  Button,
  Table,
  Statistic,
  Row,
  Col,
  Typography,
  Tabs,
  Select,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  useInventoryReportsServiceGetSalesSummary,
  useInventoryReportsServiceDownloadSalesSummary,
} from "api/queries";
import dayjs from "dayjs";

const { Title } = Typography;

const GROUP_OPTIONS = [
  { label: "Daily", value: "DAY" },
  { label: "Weekly", value: "WEEK" },
  { label: "Monthly", value: "MONTH" },
];

const PERIOD_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "This Week", value: "This Week" },
  { label: "This Month", value: "This Month" },
  { label: "This Year", value: "This Year" },
];

const SalesSummaryReport = () => {
  const [groupBy, setGroupBy] = useState<"DAY" | "WEEK" | "MONTH">("DAY");
  const [startDate, setStartDate] = useState(dayjs().add(-30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [periodFilter, setPeriodFilter] = useState("Custom");

  const { data, isLoading, isError, refetch } =
    useInventoryReportsServiceGetSalesSummary({
      startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      groupBy,
    });

  const { mutate: handleDownloadReport } = useInventoryReportsServiceDownloadSalesSummary();

  useEffect(() => {
    refetch();
    // eslint-disable-next-line
  }, [startDate, endDate, groupBy]);

  // Period filter logic
  const handlePeriodChange = (value: string) => {
    setPeriodFilter(value);
    const now = dayjs();
    switch (value) {
      case "Today":
        setStartDate(now.startOf("day"));
        setEndDate(now.endOf("day"));
        break;
      case "This Week":
        setStartDate(now.startOf("week"));
        setEndDate(now.endOf("week"));
        break;
      case "This Month":
        setStartDate(now.startOf("month"));
        setEndDate(now.endOf("month"));
        break;
      case "This Year":
        setStartDate(now.startOf("year"));
        setEndDate(now.endOf("year"));
        break;
      default:
        // Custom
        break;
    }
  };

  const handleStartDateChange = (date: any) => {
    setStartDate(date || dayjs());
    setPeriodFilter("Custom");
  };
  const handleEndDateChange = (date: any) => {
    setEndDate(date || dayjs());
    setPeriodFilter("Custom");
  };

  const handleGroupChange = (value: "DAY" | "WEEK" | "MONTH") => {
    setGroupBy(value);
    // Optionally adjust date picker mode here for week/month
  };

  const handleDownload = () => {
    handleDownloadReport(
      {
        startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        endDate: endDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
        groupBy,
      },
      {
        onSuccess: (response: any) => {
          if (response?.downloadUrl) {
            // Create a temporary link and trigger download
            const link = document.createElement("a");
            link.href = response.downloadUrl;
            link.setAttribute("download", "sales_summary_report.xlsx");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
      }
    );
  };

  const summaryData = data
    ? {
        totalSales: data.totalSales,
        totalInvoices: data.totalInvoices,
        averageInvoiceValue: data.averageInvoiceValue ?? 0,
      }
    : {
        totalSales: 0,
        totalInvoices: 0,
        averageInvoiceValue: 0,
      };

  const topProducts =
    data?.topSellingProducts?.map((item: any, idx: number) => ({
      key: item.productId || idx,
      product: item.productName,
      quantity: item.quantitySold,
      revenue: item.totalRevenue,
    })) || [];

  const periodSummaries =
    data?.periodSummaries?.map((item: any, idx: number) => ({
      key: item.period || idx,
      period: item.period,
      totalAmount: item.totalAmount,
      invoiceCount: item.invoiceCount,
      averageInvoiceValue: item.averageInvoiceValue ?? 0,
    })) || [];

  const columns = [
    { title: "Product", dataIndex: "product", key: "product" },
    { title: "Quantity Sold", dataIndex: "quantity", key: "quantity" },
    {
      title: "Revenue",
      dataIndex: "revenue",
      key: "revenue",
      render: (value: number) => value?.toLocaleString(),
    },
  ];

  const periodColumns = [
    {
      title: groupBy === "DAY" ? "Date" : groupBy === "WEEK" ? "Week" : "Month",
      dataIndex: "period",
      key: "period",
    },
    {
      title: "Total Sales",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value: number) => value?.toLocaleString(),
    },
    { title: "Total Invoices", dataIndex: "invoiceCount", key: "invoiceCount" },
    {
      title: "Average Invoice Value",
      dataIndex: "averageInvoiceValue",
      key: "averageInvoiceValue",
    },
  ];

  const tabItems = [
    {
      key: "top-products",
      label: "Top-Selling Products",
      children: (
        <Table
          columns={columns}
          dataSource={topProducts}
          pagination={false}
          loading={isLoading}
        />
      ),
    },
    {
      key: "period-sales",
      label: "Period Sales",
      children: (
        <Table
          columns={periodColumns}
          dataSource={periodSummaries}
          pagination={false}
          loading={isLoading}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Title level={2}>Sales Summary</Title>
            <Space>
              <Select
                value={groupBy}
                onChange={handleGroupChange}
                options={GROUP_OPTIONS}
                style={{ width: 120 }}
              />
              <Select
                value={periodFilter}
                onChange={handlePeriodChange}
                options={PERIOD_OPTIONS}
                style={{ width: 120 }}
                placeholder="Period"
              />
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                style={{ width: 120 }}
                placeholder="From"
              />
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                style={{ width: 120 }}
                placeholder="To"
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={isLoading}
                onClick={handleDownload}
              >
                Download Report
              </Button>
            </Space>
          </Space>

          {isError ? (
            <div style={{ color: "red" }}>Failed to load data.</div>
          ) : (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Card loading={isLoading}>
                    <Statistic
                      title="Total Sales"
                      value={summaryData.totalSales}
                      precision={2}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card loading={isLoading}>
                    <Statistic
                      title="Total Invoices"
                      value={summaryData.totalInvoices}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card loading={isLoading}>
                    <Statistic
                      title="Average Invoice Value"
                      value={summaryData.averageInvoiceValue}
                      precision={2}
                    />
                  </Card>
                </Col>
              </Row>
              <Tabs items={tabItems} />
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default SalesSummaryReport;
